import { Mail, Phone, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

/**
 * Component to let users choose their preferred signup method
 * Options: Email verification or SMS verification
 */

export const SignupMethodSelector = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-2"
        >
          <h1 className="text-2xl font-bold">Criar Conta</h1>
          <p className="text-muted-foreground">
            Escolha como deseja verificar sua identidade
          </p>
        </motion.div>

        <div className="space-y-3">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card 
              className="p-5 hover:border-primary hover:shadow-lg transition-all cursor-pointer group"
              onClick={() => navigate('/signup')}
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Mail className="w-7 h-7 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">Email</h3>
                  <p className="text-sm text-muted-foreground">
                    Receba o código no seu email
                  </p>
                  <p className="text-xs text-primary mt-1">
                    ✓ Gratuito • ✓ Recomendado
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card 
              className="p-5 hover:border-primary hover:shadow-lg transition-all cursor-pointer group"
              onClick={() => navigate('/signup-sms')}
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Phone className="w-7 h-7 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">SMS</h3>
                  <p className="text-sm text-muted-foreground">
                    Receba o código via mensagem de texto
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    ✓ Mais rápido • ✓ Alternativa
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card 
              className="p-5 hover:border-primary hover:shadow-lg transition-all cursor-pointer group"
              onClick={() => navigate('/signup-whatsapp')}
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-[#25D366]/10 rounded-full flex items-center justify-center group-hover:bg-[#25D366]/20 transition-colors">
                  <MessageCircle className="w-7 h-7 text-[#25D366]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg">WhatsApp</h3>
                  <p className="text-sm text-muted-foreground">
                    Receba o código via WhatsApp Business
                  </p>
                  <p className="text-xs text-[#25D366] mt-1">
                    ✓ Instantâneo • ✓ Popular
                  </p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center"
        >
          <p className="text-sm text-muted-foreground">
            Já tem uma conta?{" "}
            <Button
              variant="link"
              className="p-0 h-auto"
              onClick={() => navigate('/login')}
            >
              Fazer login
            </Button>
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center text-xs text-muted-foreground bg-muted/30 rounded-lg p-3"
        >
          <p>
            💡 Se não receber email, você pode usar SMS ou WhatsApp como alternativa
          </p>
        </motion.div>
      </div>
    </div>
  );
};